import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import VerifiedBadge from './VerifiedBadge';
import api from '../api/client';

/** NeoUI — followers / following list dialog (clickable profile stats). */
const FollowListDialog = ({ open, onOpenChange, username, type }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const title = type === 'followers' ? 'Follower' : 'Seguiti';

  useEffect(() => {
    if (!open || !username) return;
    setLoading(true);
    api.get(`/users/${username}/${type}`)
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [open, username, type]);

  const goTo = (u) => {
    onOpenChange(false);
    navigate(`/u/${u.username}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[70vh] sm:max-w-sm" data-testid="follow-list-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 gold-text" /> {title}
          </DialogTitle>
          <DialogDescription className="sr-only">{title} di @{username}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto" data-testid="follow-list-items">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : users.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="follow-list-empty">
              {type === 'followers' ? 'Ancora nessun follower.' : 'Non segue ancora nessuno.'}
            </p>
          ) : (
            users.map((u) => (
              <button
                key={u.id}
                onClick={() => goTo(u)}
                className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-muted"
                data-testid={`follow-list-item-${u.username}`}
              >
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt={u.username} className="h-10 w-10 shrink-0 rounded-full object-cover object-center" loading="lazy" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-rose-400 text-sm font-semibold text-white">
                    {(u.username || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1 truncate text-sm font-semibold">
                    {u.username}
                    {u.is_verified_creator && <VerifiedBadge size={13} />}
                  </p>
                  {u.bio && <p className="truncate text-xs text-muted-foreground">{u.bio}</p>}
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FollowListDialog;
